import { useCallback } from 'react';
import { STORAGE } from '../constants';
import { LocalStorageService } from '../services/local-storage.service';

interface CampaignData {
  name: string;
  companies: string;
  account: string;
  keyBase: string;
  typeCampaign: 'DEFAULT' | 'FALLBACK' | 'FLOW';
  headers: string;
  programing?: boolean;
  dateHour?: string;
  timeZone?: string;
  dosing?: boolean;
  initHour?: string;
  finishHour?: string;
  uniqueSend?: boolean;
  blacklistSwitch?: boolean;
  blacklist: string[];
  channel: string;
  type: string;
  msgSMS: string;
  total_character: number;
  total_credits: number;
  masks: string;
  complementsSwitch: boolean;
  complements: string;
  phone_number: string;
  msg_WHATSAPP_LINK: string;
  url_LINK: string;
  accents: boolean;
  previewRCS?: any;
  optionsExamples?: any;
  typeRCS: string;
  templatesRCS: string;
  agentsRCS: string;
  templatesWa: string;
  accountsWa: string;
  varsWa: [];
  secondReprocess: boolean;
  channelfallback: string;
  trySecondFallback: boolean;
  secondChannelfallback: string;
}

const generalData = (data: CampaignData): any => {
  const session = LocalStorageService.get(STORAGE.SESSION);
  const result: any = {
    account: data.account || '',
    companie: data.companies || '',
    user: session._id || '',
    name: data.name || '',
    db: data.keyBase || '',
    type: data.typeCampaign || '',
    phone_number_column: data.headers || '',
    send_single_messages: data.uniqueSend || false
  };

  if (data.programing) {
    result.message_schedule = {
      start_date: data.dateHour ?? '',
      timezone: data.timeZone ?? ''
    };
  }

  if (data.dosing) {
    result.message_date_window = {
      start_date: data.initHour ?? '',
      end_date: data.finishHour ?? ''
    };
  }

  if (data.blacklistSwitch) {
    result.blacklists = data.blacklist || [];
  }
  return result;
};

const useCampaignsStructures = () => {
  const structureData = useCallback((data: CampaignData) => {
    if (data.typeCampaign === 'FALLBACK') {
      const result = generalData(data);
      const extractVariables = (obj: any, variables: Set<string>) => {
        const regex = /{{\s*([\w\-]+)\s*}}/g;

        const traverse = (value: any) => {
          if (typeof value === 'string') {
            let match;
            while ((match = regex.exec(value)) !== null) {
              variables.add(match[1]);
            }
          } else if (Array.isArray(value)) {
            value.forEach((item) => traverse(item));
          } else if (typeof value === 'object' && value !== null) {
            Object.values(value).forEach((val) => traverse(val));
          }
        };

        traverse(obj);
      };

      const channels: any[] = [];

      // Canal SMS
      if (
        data.channel === 'sms' ||
        data.channelfallback === 'sms' ||
        data.secondChannelfallback === 'sms'
      ) {
        channels.push({
          type: 'SMS',
          sms: {
            message: data.msgSMS || '',
            total_character: data.total_character || 0,
            total_credits: data.total_credits || 0,
            mask: data.masks || '',
            ...(data.complementsSwitch && {
              complement: {
                type: data.complements || 'WHATSAPP_LINK',
                ...(data.complements === 'WHATSAPP_LINK'
                  ? {
                      whatsapp_link: {
                        phone_number: data.phone_number ? `+52${data.phone_number}` : '',
                        text: data.msg_WHATSAPP_LINK || ''
                      }
                    }
                  : {}),
                ...(data.complements === 'LINK' ? { link: data.url_LINK || '' } : {})
              }
            }),
            has_accents: data.accents || false
          },
          index: data.channel === 'sms' ? 0 : data.channelfallback === 'sms' ? 1 : 2
        });
      }

      // Canal WhatsApp
      if (
        data.channel === 'wa' ||
        data.channelfallback === 'wa' ||
        data.secondChannelfallback === 'wa'
      ) {
        channels.push({
          type: 'WHATSAPP',
          whatsapp: {
            template: data.templatesWa || '',
            waba: data.accountsWa || '',
            vars:
              data.varsWa &&
              Object.values(data.varsWa).every(
                (val) => typeof val === 'object' && val !== null && Object.keys(val).length === 0
              )
                ? {}
                : Object.fromEntries(
                    Object.entries(data.varsWa).map(([key, value]) => [key.toLowerCase(), value])
                  )
          },
          index: data.channel === 'wa' ? 0 : data.channelfallback === 'wa' ? 1 : 2
        });
      }

      // Canal RCS
      if (
        data.channel === 'rcs' ||
        data.channelfallback === 'rcs' ||
        data.secondChannelfallback === 'rcs'
      ) {
        const variablesFound = new Set<string>();
        extractVariables(data.previewRCS?.preview || [], variablesFound);

        const optionsExamples = data.optionsExamples || [];
        const vars: Record<string, string> = {};
        const missingVars: string[] = [];

        variablesFound.forEach((variable) => {
          const foundOption = optionsExamples.find((example: any) => example.header === variable);
          if (foundOption) {
            vars[`{{${variable}}}`] = foundOption.header;
          } else {
            missingVars.push(variable);
          }
        });

        const typeKey = data.typeRCS.toLowerCase();
        const index = data.channel === 'rcs' ? 0 : data.channelfallback === 'rcs' ? 1 : 2;

        channels.push({
          type: data.typeRCS.toUpperCase(),
          [typeKey]: {
            template: data.templatesRCS || '',
            agent: data.agentsRCS || '',
            vars: Object.keys(vars).length ? vars : {}
          },
          index
        });

        if (missingVars.length > 0) {
          return {
            error: 'variables_not_found',
            variables: missingVars
          };
        }
      }

      result.channels = channels;

      return result;
    }
    return null;
  }, []);

  return { structureData };
};
export default useCampaignsStructures;
